import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Wrench, AlertTriangle, Clock, ShieldCheck, Plus, Trash2, CheckCircle2, Loader2, Info, ZoomIn, X, ExternalLink, Radar, Mountain, Gauge, Compass } from "lucide-react";
import { DroneIcon } from "@/components/DroneIcon";
import HeroImageUpload from "@/components/HeroImageUpload";
import AssetTrackMap from "@/components/AssetTrackMap";

const imgUrl = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`;

const CASA_GUIDANCE = {
  drone: "Maintain in accordance with the manufacturer's specifications and your ReOC maintenance procedures. A Maintenance Controller must oversee all scheduled/unscheduled maintenance and record defects (CASR Part 101 / AC 101-01).",
  helicopter: "Periodic inspection every 100 hrs or 12 months, whichever comes first, under the CASA Maintenance Schedule; private rotorcraft may use an annual inspection. Track Airworthiness Directives (ADs) (CASR Part 42 / CAAP 42B-01).",
  aeroplane: "Periodic inspection every 100 hrs or 12 months, whichever comes first (100-hourly for hire/training); annual inspection for private aircraft <5,700 kg. Track Airworthiness Directives (ADs) (CASR Part 42 / CAAP 42B-01).",
  battery: "Track charge cycles and inspect LiPo cells for swelling/damage. Store at storage voltage and retire per the manufacturer's cycle limit.",
  vehicle: "Service per the manufacturer's schedule (e.g. every 10,000 km / 6 months). Keep registration and roadworthy inspection current.",
  generator: "Service per manufacturer run-hours (e.g. oil & filter every 100–200 hrs). Test under load regularly and log fuel/oil.",
  charger: "Inspect leads and connectors; verify output per the manufacturer's recommendations.",
  camera: "Calibrate and clean sensors/lenses per the manufacturer's recommendations.",
  sensor: "Calibrate per the manufacturer's recommendations and before precision missions.",
  other: "Maintain and inspect per the manufacturer's recommendations.",
};

function dueItems(asset) {
  const out = [];
  const now = new Date();
  if (asset.maintenance_interval_hours > 0) {
    const dueAt = (asset.last_service_hours || 0) + asset.maintenance_interval_hours;
    const remaining = dueAt - (asset.flight_hours || 0);
    out.push({ kind: "Hours", label: `${remaining.toFixed(1)} h remaining`, sub: `next due at ${dueAt.toFixed(0)} h`, level: remaining < 0 ? "overdue" : remaining <= 10 ? "soon" : "ok" });
  }
  if (asset.maintenance_interval_days > 0 && asset.last_service_date) {
    const due = new Date(asset.last_service_date); due.setDate(due.getDate() + asset.maintenance_interval_days);
    const days = Math.ceil((due - now) / 86400000);
    out.push({ kind: "Calendar", label: `${days} days remaining`, sub: `due ${due.toLocaleDateString("en-AU")}`, level: days < 0 ? "overdue" : days <= 30 ? "soon" : "ok" });
  }
  return out;
}
const LEVEL = { ok: "bg-lime-100 text-lime-700 border-lime-200", soon: "bg-amber-100 text-amber-700 border-amber-200", overdue: "bg-red-100 text-red-700 border-red-200" };

function Field({ label, value }) {
  if (!value && value !== 0) return null;
  return <div><dt className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</dt><dd className="text-slate-800 mt-0.5">{value}</dd></div>;
}

export default function AssetDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [logs, setLogs] = useState([]);
  const [defects, setDefects] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zoomImg, setZoomImg] = useState(null);
  const [mOpen, setMOpen] = useState(false);
  const [dOpen, setDOpen] = useState(false);
  const [mForm, setMForm] = useState({ date: new Date().toISOString().slice(0, 10), hours: 0, type: "scheduled", description: "", performed_by: "" });
  const [dForm, setDForm] = useState({ reported_date: new Date().toISOString().slice(0, 10), description: "", severity: "minor", status: "open" });

  const load = useCallback(async () => {
    try {
      const [a, m, d, t] = await Promise.all([api.get(`/assets/${id}`), api.get(`/assets/${id}/maintenance`), api.get(`/assets/${id}/defects`), api.get(`/tracking/history/${id}`)]);
      setAsset(a.data); setLogs(m.data); setDefects(d.data); setTracks(t.data || []);
    } catch { toast.error("Failed to load asset"); } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const addM = async () => {
    try { await api.post(`/assets/${id}/maintenance`, { ...mForm, hours: +mForm.hours || 0 }); toast.success("Service logged"); setMOpen(false); load(); }
    catch { toast.error("Failed"); }
  };
  const delM = async (lid) => { try { await api.delete(`/assets/${id}/maintenance/${lid}`); load(); } catch { toast.error("Failed"); } };
  const addD = async () => {
    if (!dForm.description.trim()) { toast.error("Describe the defect"); return; }
    try { await api.post(`/assets/${id}/defects`, dForm); toast.success("Defect logged"); setDOpen(false); load(); }
    catch { toast.error("Failed"); }
  };
  const toggleD = async (d) => {
    const upd = { ...d, status: d.status === "open" ? "closed" : "open", resolved_date: d.status === "open" ? new Date().toISOString().slice(0, 10) : "" };
    try { await api.put(`/assets/${id}/defects/${d.id}`, upd); load(); } catch { toast.error("Failed"); }
  };
  const delD = async (did) => { try { await api.delete(`/assets/${id}/defects/${did}`); load(); } catch { toast.error("Failed"); } };
  const clearTracks = async () => { try { await api.delete(`/tracking/history/${id}`); setTracks([]); toast.success("Tracking history cleared"); } catch { toast.error("Failed"); } };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-lime-500" /></div>;
  if (!asset) return null;
  const due = dueItems(asset);
  const openDefects = defects.filter((d) => d.status === "open");
  const gallery = [asset.image_path, ...(asset.images || [])].filter(Boolean);

  return (
    <div className="space-y-6 fade-up" data-testid="asset-detail-page">
      <button onClick={() => navigate("/assets")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900" data-testid="asset-back-btn"><ArrowLeft className="h-4 w-4" /> All assets</button>

      <div className="grid lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2 space-y-6">
          {/* Header */}
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="h-48 bg-slate-100 flex items-center justify-center relative group">
              {asset.image_path ? (
                <>
                  <button type="button" onClick={() => setZoomImg(asset.image_path)} className="h-full w-full cursor-zoom-in" data-testid="asset-hero-zoom"><img src={imgUrl(asset.image_path)} alt={asset.name} className="h-full w-full object-cover" /></button>
                  <span className="absolute top-2 right-2 h-8 w-8 rounded-full bg-slate-900/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"><ZoomIn className="h-4 w-4" /></span>
                </>
              ) : (
                <HeroImageUpload testid="asset-hero" label="Add asset photo" rounded="rounded-none" className="h-full w-full"
                  imagePath={null}
                  onUploaded={async (path) => { await api.put(`/assets/${id}`, { ...asset, image_path: path }); load(); }} />
              )}
            </div>
            {gallery.length > 1 && (
              <div className="flex gap-2 p-3 border-t border-slate-100 overflow-x-auto" data-testid="asset-gallery">
                {gallery.map((p) => (
                  <button key={p} type="button" onClick={() => setZoomImg(p)} className="h-16 w-16 shrink-0 rounded-lg border border-slate-200 overflow-hidden hover:ring-2 hover:ring-lime-400 transition" data-testid="asset-gallery-thumb">
                    <img src={imgUrl(p)} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            <div className="p-6">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h1 className="text-2xl font-bold font-heading text-slate-900">{asset.name}</h1>
                  <p className="text-slate-500 capitalize">{asset.category}{asset.model ? ` · ${asset.model}` : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  {asset.asset_type && <Badge className="border bg-lime-100 text-lime-700 border-lime-200" data-testid="asset-type-badge">{asset.asset_type}</Badge>}
                  <Badge className="capitalize border bg-slate-100 text-slate-700 border-slate-200">{asset.status?.replace("_", " ")}</Badge>
                  {(asset.icao24 || asset.registration) && (
                    <Button size="sm" data-testid="track-live-btn"
                      onClick={() => navigate(`/tracking?q=${encodeURIComponent(asset.icao24 || asset.registration)}`)}
                      className="bg-slate-900 hover:bg-slate-800 text-white"><Radar className="h-4 w-4 mr-1.5" /> Track live</Button>
                  )}
                </div>
              </div>
              <dl className="grid sm:grid-cols-3 gap-4 mt-6">
                <Field label="Registration" value={asset.registration} />
                <Field label="Tracking code (ICAO24)" value={asset.icao24 ? asset.icao24.toUpperCase() : null} />
                <Field label="Serial no." value={asset.serial_no} />
                <Field label="Flight hours" value={`${asset.flight_hours || 0} h`} />
                <Field label="Cycles" value={asset.cycles || 0} />
                <Field label="Batteries" value={asset.battery_count || 0} />
                <Field label="Purchase date" value={asset.purchase_date} />
                <Field label="Airworthiness expiry" value={asset.airworthiness_expiry} />
                <Field label="Maintenance controller" value={asset.maintenance_controller} />
                <Field label="Last service" value={asset.last_service_date ? `${asset.last_service_date} @ ${asset.last_service_hours || 0}h` : "—"} />
              </dl>
              {asset.notes && <p className="text-sm text-slate-600 mt-4 border-t border-slate-100 pt-4">{asset.notes}</p>}
            </div>
          </div>

          {/* Maintenance history */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><Wrench className="h-5 w-5 text-lime-500" /> Maintenance history</h2>
              <Button size="sm" onClick={() => setMOpen(true)} data-testid="add-maintenance-btn" className="bg-slate-900 hover:bg-slate-800 text-white"><Plus className="h-4 w-4 mr-1" /> Log service</Button>
            </div>
            {logs.length === 0 ? <p className="text-sm text-slate-400">No maintenance logged yet.</p> : (
              <div className="divide-y divide-slate-100" data-testid="maintenance-list">
                {logs.map((l) => (
                  <div key={l.id} className="py-3 flex items-start justify-between gap-3" data-testid={`maintenance-${l.id}`}>
                    <div>
                      <div className="text-sm font-medium text-slate-800">{l.description || l.type} <Badge variant="outline" className="ml-1 text-[10px] capitalize border-slate-200">{l.type}</Badge></div>
                      <p className="text-xs text-slate-400 mt-0.5">{l.date} · {l.hours || 0}h{l.performed_by ? ` · ${l.performed_by}` : ""}</p>
                    </div>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-300 hover:text-red-600" onClick={() => delM(l.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Defects / squawks */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-500" /> Defects / squawks {openDefects.length > 0 && <Badge className="bg-red-100 text-red-700 border-red-200 border">{openDefects.length} open</Badge>}</h2>
              <Button size="sm" onClick={() => setDOpen(true)} data-testid="add-defect-btn" className="bg-slate-900 hover:bg-slate-800 text-white"><Plus className="h-4 w-4 mr-1" /> Report</Button>
            </div>
            {defects.length === 0 ? <p className="text-sm text-slate-400">No defects reported.</p> : (
              <div className="divide-y divide-slate-100" data-testid="defects-list">
                {defects.map((d) => (
                  <div key={d.id} className="py-3 flex items-start justify-between gap-3" data-testid={`defect-${d.id}`}>
                    <div className="min-w-0">
                      <p className={`text-sm ${d.status === "closed" ? "line-through text-slate-400" : "text-slate-800"}`}>{d.description}</p>
                      <p className="text-xs text-slate-400 mt-0.5 capitalize">{d.reported_date} · {d.severity}{d.status === "closed" && d.resolved_date ? ` · resolved ${d.resolved_date}` : ""}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => toggleD(d)} data-testid={`toggle-defect-${d.id}`} className="h-7 text-xs">{d.status === "open" ? "Close" : "Reopen"}</Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-300 hover:text-red-600" onClick={() => delD(d.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tracking history */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="tracking-history-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><Radar className="h-5 w-5 text-lime-500" /> Tracking history {tracks.length > 0 && <Badge className="bg-slate-100 text-slate-700 border-slate-200 border">{tracks.length}</Badge>}</h2>
              <div className="flex items-center gap-2">
                {(asset.icao24 || asset.registration) && (
                  <Button size="sm" variant="outline" data-testid="track-live-btn-2" onClick={() => navigate(`/tracking?q=${encodeURIComponent(asset.icao24 || asset.registration)}`)}><Radar className="h-4 w-4 mr-1" /> Track live</Button>
                )}
                {tracks.length > 0 && <Button size="sm" variant="ghost" className="text-slate-400 hover:text-red-600" data-testid="clear-tracks-btn" onClick={clearTracks}><Trash2 className="h-3.5 w-3.5 mr-1" /> Clear</Button>}
              </div>
            </div>
            {tracks.length === 0 ? (
              <p className="text-sm text-slate-400">No tracking positions recorded yet. Use <span className="font-medium text-slate-600">Track live</span> to capture this asset's live position over time.</p>
            ) : (
              <>
                <AssetTrackMap tracks={tracks} />
                <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto" data-testid="tracking-history-list">
                  {tracks.map((t) => (
                    <div key={t.id} className="py-2.5 flex items-center justify-between gap-3 text-sm" data-testid={`track-${t.id}`}>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-800 tabular-nums">{t.lat?.toFixed(4)}, {t.lon?.toFixed(4)}</p>
                        <p className="text-xs text-slate-400">
                          {t.last_contact ? new Date(t.last_contact * 1000).toLocaleString("en-AU") : new Date(t.recorded_at).toLocaleString("en-AU")}
                          {t.callsign ? ` · ${t.callsign}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500 shrink-0">
                        {t.altitude != null && <span className="inline-flex items-center gap-1"><Mountain className="h-3 w-3" /> {Math.round(t.altitude)} m</span>}
                        {t.velocity != null && <span className="inline-flex items-center gap-1"><Gauge className="h-3 w-3" /> {Math.round(t.velocity * 1.94384)} kt</span>}
                        {t.heading != null && <span className="inline-flex items-center gap-1"><Compass className="h-3 w-3" /> {Math.round(t.heading)}°</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right rail: compliance */}
        <div className="space-y-4 lg:sticky lg:top-24">
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2 mb-4"><ShieldCheck className="h-5 w-5 text-lime-500" /> Compliance</h2>
            {due.length === 0 ? (
              <p className="text-sm text-slate-400">No maintenance interval set. Edit the asset to add an hours or calendar interval.</p>
            ) : (
              <div className="space-y-3" data-testid="compliance-panel">
                {due.map((x) => (
                  <div key={x.kind} className={`rounded-lg border p-3 ${LEVEL[x.level]}`} data-testid={`due-${x.kind.toLowerCase()}`}>
                    <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider"><Clock className="h-3.5 w-3.5" /> {x.kind} {x.level === "overdue" ? "· OVERDUE" : x.level === "soon" ? "· Due soon" : ""}</div>
                    <p className="font-heading font-semibold mt-1">{x.label}</p>
                    <p className="text-xs opacity-80">{x.sub}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="bg-sky-50 border border-sky-200 rounded-2xl p-5" data-testid="casa-guidance">
            <div className="flex items-center gap-2 text-sky-800 font-heading font-semibold mb-2"><Info className="h-4 w-4" /> CASA guidance</div>
            <p className="text-sm text-sky-900/80 leading-relaxed">{CASA_GUIDANCE[asset.category] || CASA_GUIDANCE.other}</p>
            <p className="text-[11px] text-sky-700/70 mt-3">Guidance only — always confirm current requirements at casa.gov.au.</p>
          </div>
        </div>
      </div>

      {/* Log service dialog */}
      <Dialog open={mOpen} onOpenChange={setMOpen}>
        <DialogContent className="max-w-md" data-testid="maintenance-dialog">
          <DialogHeader><DialogTitle className="font-heading">Log a service</DialogTitle><DialogDescription>Record a scheduled or unscheduled maintenance event for this asset.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-500">Date</Label><Input type="date" value={mForm.date} onChange={(e) => setMForm({ ...mForm, date: e.target.value })} className="mt-1" data-testid="maintenance-date" /></div>
              <div><Label className="text-xs text-slate-500">Hours at service</Label><Input type="number" step="0.1" value={mForm.hours} onChange={(e) => setMForm({ ...mForm, hours: e.target.value })} className="mt-1" data-testid="maintenance-hours" /></div>
            </div>
            <div><Label className="text-xs text-slate-500">Type</Label>
              <Select value={mForm.type} onValueChange={(v) => setMForm({ ...mForm, type: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{["scheduled", "unscheduled", "inspection", "repair"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs text-slate-500">Description</Label><Textarea value={mForm.description} onChange={(e) => setMForm({ ...mForm, description: e.target.value })} className="mt-1" rows={2} data-testid="maintenance-desc" /></div>
            <div><Label className="text-xs text-slate-500">Performed by</Label><Input value={mForm.performed_by} onChange={(e) => setMForm({ ...mForm, performed_by: e.target.value })} className="mt-1" placeholder="LAME / technician" /></div>
            <Button onClick={addM} className="w-full bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold" data-testid="save-maintenance-btn">Save service</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Defect dialog */}
      <Dialog open={dOpen} onOpenChange={setDOpen}>
        <DialogContent className="max-w-md" data-testid="defect-dialog">
          <DialogHeader><DialogTitle className="font-heading">Report a defect</DialogTitle><DialogDescription>Log a defect or squawk against this asset.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-slate-500">Description</Label><Textarea value={dForm.description} onChange={(e) => setDForm({ ...dForm, description: e.target.value })} className="mt-1" rows={2} data-testid="defect-desc" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-500">Date</Label><Input type="date" value={dForm.reported_date} onChange={(e) => setDForm({ ...dForm, reported_date: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs text-slate-500">Severity</Label>
                <Select value={dForm.severity} onValueChange={(v) => setDForm({ ...dForm, severity: v })}><SelectTrigger className="mt-1" data-testid="defect-severity"><SelectValue /></SelectTrigger>
                  <SelectContent>{["minor", "major", "grounding"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <Button onClick={addD} className="w-full bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold" data-testid="save-defect-btn">Report defect</Button>
          </div>
        </DialogContent>
      </Dialog>

      {zoomImg && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4 sm:p-8" onClick={() => setZoomImg(null)} data-testid="asset-image-lightbox">
          <img src={imgUrl(zoomImg)} alt={asset.name} className="max-h-full max-w-full object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
          <button onClick={() => setZoomImg(null)} className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors" data-testid="asset-lightbox-close"><X className="h-5 w-5" /></button>
          <a href={imgUrl(zoomImg)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="absolute bottom-4 right-4 inline-flex items-center gap-1.5 text-xs text-white/80 hover:text-white underline" data-testid="asset-lightbox-newtab"><ExternalLink className="h-3.5 w-3.5" /> Open in new tab</a>
        </div>
      )}
    </div>
  );
}
